import AttendanceModel from "#src/models/AttendanceModel.ts";
import { CreditModel } from "#src/models/CreditModel.ts";
import { CustomerServiceItemModel } from "#src/models/CustomerServiceItemModel.ts";
import EvaluationModel from "#src/models/EvaluationModel.ts";
import GroupRegistrationModel from "#src/models/GroupRegistrationModel.ts";
import { InvoiceItemModel } from "#src/models/InvoiceItemModel.ts";
import InvoiceModel from "#src/models/InvoiceModel.ts";
import MembershipModel from "#src/models/MembershipModel.ts";
import PaymentModel from "#src/models/PaymentModel.ts";
import PrivateSerieModel from "#src/models/PrivateSerieModel.ts";
import { RecurringChargeModel } from "#src/models/RecurringChargeModel.ts";
import { RecurringCreditModel } from "#src/models/RecurringCreditModel.ts";
import { SettingModel } from "#src/models/SettingModel.ts";
import TokenModel from "#src/models/TokenModel.ts";
import { moment } from "#src/utils/dateTime.ts";
import {
  DepartmentPayload,
  Gender,
  GroupRegistrationStatus,
  HealthQuestionResponse,
  Level,
  MembershipStatus,
  PermissionType,
  PrivateSerieStatus,
  SettingType,
  StripeType,
  UserAgreement,
} from "#types/code-generator.ts";
import type { StripeToken } from "#types/stripeToken.d.ts";
import type { UsaSwimmingMember } from "#types/usa-swimming.d.ts";

export interface Cart {
  students: Array<{ id: string; firstName: string }>;
  groupRegistrations: Array<{
    id: string;
    class: { id: string };
    status: string;
    student: { id: string; firstName: string };
  }>;
  privateSeries: [];
}

export const isUser = (user): user is UserModel => {
  return typeof user.firstName !== "undefined" || user instanceof UserModel;
};

class UserModel {
  accountOwner: UserModel;
  active: boolean;
  address1: string;
  address2: string;
  adyenToken: StripeToken;
  allowAssignment: boolean;
  allowMarketing: boolean;
  attendancesAsAttended: AttendanceModel[];
  avatar: {
    id: string;
    url: string;
    name: string;
  };
  balance: number = 0;
  bio: string;
  birthDate: Date;
  cart: Cart;
  city: string;
  createdAt: Date;
  credits: CreditModel[] = [];
  customNotes: string[];
  customerServiceItems: CustomerServiceItemModel[];
  departments: DepartmentPayload[];
  email: string = "";
  emergencyContactName: string;
  emergencyContactPhone: string;
  evaluations: EvaluationModel[];
  // events: EventModel[] = [];
  firstName: string = "";
  futureInvoiceBalance: number = 0;
  gender: Gender;
  groupRegistrations: GroupRegistrationModel[] = [];
  healthQuestionResponses: HealthQuestionResponse[] = [];
  // healthConcerns: string;
  hideInFilters: boolean = false;
  id: string;
  invoiceItems: InvoiceItemModel[];
  invoices: InvoiceModel[] = [];
  isStudent: boolean;
  jobCreditBalance: number;
  lastAccess: Date;
  lastName: string = "";
  level: Level;
  linkedAccounts: UserModel[];
  memberships: MembershipModel[] = [];
  membershipsRoster: MembershipModel[] = [];
  memberSince: Date;
  note: string;
  organization: {
    id: string;
    departments: {
      id: string;
    }[];
    logo: {
      url: string;
    };
    stripe: StripeType;
    timezone: string;
    title: string;
    paymentProcessing: {
      enableAch: boolean;
      achCredit: number;
      achCreditBillingCategory: {
        title: string;
      };
    };
  };
  organizationOwner: {
    id: string;
    lastFailedPayment: Date;
  };
  // organizations: OrganizationModel[] = [];
  payments: PaymentModel[];
  permissions: PermissionType[];
  phone: string;
  primaryAccount: UserModel;
  privateGroupRegistrations?: GroupRegistrationModel[];
  privateSeries: PrivateSerieModel[] = [];
  privateSerieAccountOwner: PrivateSerieModel[] = [];
  privateSerieInstructor: PrivateSerieModel[] = [];
  profile: UserModel;
  profiles: {
    id: string;
    createdAt?: Date;
    updatedAt?: Date;
    email: string;
    lastAccess: Date;
    level: Level;
    organization: {
      id: string;
      subdomain: string;
      title: string;
      color: string;
      logo: {
        url: string;
      };
    };
  }[];
  pushNotificationToken: string;
  recurringCharges: RecurringChargeModel[] = [];
  recurringCredits: RecurringCreditModel[] = [];
  savedFilters: { name: string; filter: string }[] = [];
  secondaryId: string;
  settings: SettingModel[] = [];
  state: string;
  stripeCustomerId: string;
  stripeToken: StripeToken;
  studentGroupRegistrations: GroupRegistrationModel[];
  students: UserModel[];
  tokens: TokenModel[];
  updatedAt: Date;
  usaSwimmingMember: UsaSwimmingMember;
  usaSwimmingMemberId: string;
  userAgreements: UserAgreement[];
  zip: string;

  constructor(data) {
    Object.assign(this, data);
  }

  get secondaryContacts() {
    return (
      this.settings &&
      this.settings.filter((s) => s.type.includes("SecondaryContact"))
    );
  }

  get secondaryContactEmails() {
    return (
      this.settings &&
      this.settings.filter((s) => s.type === SettingType.SecondaryContactEmail)
    );
  }

  get secondaryContactPhones() {
    return (
      this.settings &&
      this.settings.filter((s) => s.type === SettingType.SecondaryContactPhone)
    );
  }

  get notificationSettings() {
    return (
      this.settings &&
      this.settings.filter(
        (s) =>
          s.type === SettingType.NotificationAccount ||
          s.type === SettingType.NotificationCustomerCancellation ||
          s.type === SettingType.NotificationLessonTransferOrAssignment ||
          s.type === SettingType.NotificationNewLesson ||
          s.type === SettingType.NotificationMarketing ||
          s.type === SettingType.OrganizationUpdates ||
          s.type === SettingType.NotificationPrivateSeriePosted
      )
    );
  }

  getSetting(setting, departmentId = null) {
    if (departmentId) {
      return (
        this.settings &&
        this.settings.find(
          (s) =>
            s.type === setting &&
            s.department &&
            s.department.id === departmentId
        )
      );
    }
    return this.settings && this.settings.find((s) => s.type === setting);
  }

  get instructorNotificationSettings() {
    return (
      this.settings &&
      this.settings.filter(
        (s) =>
          s.type === SettingType.NotificationNewLesson ||
          s.type === SettingType.NotificationLessonTransferOrAssignment ||
          s.type === SettingType.NotificationCustomerCancellation
      )
    );
  }

  get isSuperAdmin() {
    return this.level && this.level === Level.SuperAdmin;
  }

  get isSales() {
    return this.level && this.level === Level.Sales;
  }

  get isSalesOrAbove() {
    return this.isSales || this.isSuperAdmin;
  }

  get isAdminOrAbove() {
    return (
      (this.level && this.level === Level.Admin) ||
      this.isSuperAdmin ||
      this.isSales
    );
  }

  get isInstructor() {
    return this.level && this.level === Level.Instructor;
  }

  get isStaff() {
    return (
      (this.level && this.level === Level.Instructor) || this.isAdminOrAbove
    );
  }

  get activeRecurringCharges() {
    const today = new Date().getTime();

    return (
      this.recurringCharges &&
      this.recurringCharges.filter(
        (c) => new Date(c.finalDate).getTime() > today
      )
    );
  }

  get allGroupRegistrations() {
    let result = this.groupRegistrations;

    if (this.level === Level.Student) {
      if (this.accountOwner && this.accountOwner.groupRegistrations) {
        result = this.accountOwner.groupRegistrations.filter(
          (r) =>
            (r.student && r.student.id === this.id) ||
            (r.roster && r.roster.find((rs) => rs.id === this.id))
        );
      }
    }

    return result;
  }

  get activeGroupRegistrations() {
    if (this.level === Level.Student) {
      return this.allGroupRegistrations.filter(
        (r) =>
          r.status === GroupRegistrationStatus.Approved ||
          r.status === GroupRegistrationStatus.Pending ||
          (r.status === GroupRegistrationStatus.Canceled &&
            r.finalDate &&
            moment().isSameOrBefore(moment(r.finalDate), "day"))
      );
    } else {
      return this.allGroupRegistrations.filter(
        (r) =>
          r.status === GroupRegistrationStatus.Approved ||
          r.status === GroupRegistrationStatus.Pending ||
          (r.status === GroupRegistrationStatus.Canceled &&
            r.finalDate &&
            moment().isSameOrBefore(moment(r.finalDate), "day"))
      );
    }
  }

  get activeMemberships() {
    return this.memberships?.filter(
      (r) =>
        r.status === MembershipStatus.Active ||
        r.status === MembershipStatus.Pending ||
        r.status === MembershipStatus.Paused ||
        (r.finalDate && moment().isSameOrBefore(moment(r.finalDate), "day"))
    );
  }

  get allPrivateSeries() {
    let privateSeries = this.privateSeries || [];

    if (this.students && this.students.length > 0) {
      privateSeries = [
        ...privateSeries,
        ...this.students.reduce(
          (acc, s) => acc.concat(s.privateSeries || []),
          []
        ),
      ];
    }

    return privateSeries;
  }

  get activePrivateSeries() {
    return this.allPrivateSeries.filter(
      (r) =>
        r.status === PrivateSerieStatus.InProgress ||
        r.status === PrivateSerieStatus.Pending
    );
  }

  get waitlistGroupRegistrations() {
    if (this.level === Level.Student) {
      return this.allGroupRegistrations
        .filter((r) => r.status === GroupRegistrationStatus.Waitlist)
        .filter((r) => {
          if (r.student) {
            return r.student.id === this.id;
          } else if (r.roster) {
            return r.roster.find((rs) => rs.id === this.id);
          }
          return false;
        });
    } else {
      return this.allGroupRegistrations.filter(
        (r) => r.status === GroupRegistrationStatus.Waitlist
      );
    }
  }

  // hasVisited(value: string) {
  //   return this.events.find(
  //     (e) => e.type === EventType.Visited && e.value === value
  //   );
  // }
}

export default UserModel;
